<template>
  <div class="waffg-dashboard p-4 max-w-4xl mx-auto">
    <!-- Loader: Wird angezeigt, während die Action läuft -->
    <div v-if="comp_loading" class="text-center pa-10">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
      <div class="mt-4 grey--text text-subtitle-1">
        Bedürnis-Check wird erstellt...
      </div>
    </div>

    <!-- Inhalt: Wird erst gerendert, wenn Daten vorhanden sind -->
    <div v-else-if="comp_WaffGCompliance && comp_WaffGCompliance.meta">
      <h2>WaffG-Compliance (§14 WaffG)</h2>

      <!-- Info-Banner -->
      <v-alert
        type="info"
        outlined
        prominent
        border="left"
        class="mt-4 mb-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-body-1">
              <v-icon left color="info">mdi-information</v-icon>
              <strong>{{ infoText }}</strong>
            </div>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Count Cards -->
      <v-row class="mt-4">
        <!-- Sport-Waffen (Grün) -->
        <v-col cols="12" sm="6" md="4">
          <v-card color="success" dark elevation="3">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon large class="mr-3">mdi-target</v-icon>
                <div>
                  <div class="text-h3 font-weight-bold">{{ sportWeaponsCount }}</div>
                  <div class="text-h6">Sportwaffen</div>
                  <div class="text-caption">§14 WaffG - Prüfpflichtig</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Andere Waffen (Grau) -->
        <v-col cols="12" sm="6" md="4">
          <v-card color="grey darken-1" dark elevation="3">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon large class="mr-3">mdi-pistol</v-icon>
                <div>
                  <div class="text-h3 font-weight-bold">{{ otherWeaponsCount }}</div>
                  <div class="text-h6">Andere Waffen</div>
                  <div class="text-caption">Nicht prüfpflichtig</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gesamt-Waffen (Blau) - Optional -->
        <v-col cols="12" sm="6" md="4">
          <v-card color="primary" dark elevation="3">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon large class="mr-3">mdi-sword-cross</v-icon>
                <div>
                  <div class="text-h3 font-weight-bold">{{ totalWeaponsCount }}</div>
                  <div class="text-h6">Gesamt</div>
                  <div class="text-caption">Alle Waffen</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Warning bei Nicht-Sport-Waffen -->
      <v-alert
        v-if="otherWeaponsCount > 0"
        type="warning"
        outlined
        dense
        class="mt-4"
      >
        <v-icon left>mdi-alert</v-icon>
        Du hast <strong>{{ otherWeaponsCount }} Waffe(n)</strong> mit anderem Bedürfnis 
        (z.B. Jagd, Sammlung). Diese werden <strong>nicht</strong> für die Nachweispflicht 
        nach §14 WaffG geprüft.
      </v-alert>

      <!-- Falls User NUR Nicht-Sport-Waffen hat -->
      <v-alert
        v-if="sportWeaponsCount === 0 && otherWeaponsCount > 0"
        type="info"
        prominent
        border="left"
        class="mt-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-h6">Keine Sportwaffen vorhanden</div>
            <div class="mt-2">
              Du hast keine Waffen mit dem Bedürfnis "Sport" (§14 WaffG).
              Die Nachweispflicht nach §14 WaffG gilt daher <strong>nicht</strong> für dich.
            </div>
            <div class="mt-2 text-caption">
              Deine Waffen unterliegen anderen gesetzlichen Regelungen 
              (z.B. §13 Jagd, §17 Sammlung).
            </div>
          </v-col>
          <v-col class="shrink">
            <v-icon x-large color="info">mdi-shield-check</v-icon>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Legende mit Breakdown -->
      <v-expansion-panels class="mt-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon left>mdi-chart-pie</v-icon>
            Waffenübersicht nach Bedürfnis
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Kategorie</th>
                    <th>Anzahl</th>
                    <th>Prüfpflichtig?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <v-chip small color="success" dark>
                        <v-icon left small>mdi-target</v-icon>
                        Sportwaffen (§14)
                      </v-chip>
                    </td>
                    <td class="text-h6">{{ sportWeaponsCount }}</td>
                    <td>
                      <v-icon color="success">mdi-check-circle</v-icon>
                      <strong>Ja</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip small color="grey" dark>
                        <v-icon left small>mdi-pistol</v-icon>
                        Andere Bedürfnisse
                      </v-chip>
                    </td>
                    <td class="text-h6">{{ otherWeaponsCount }}</td>
                    <td>
                      <v-icon color="grey">mdi-close-circle</v-icon>
                      Nein
                    </td>
                  </tr>
                  <tr class="grey lighten-3">
                    <td><strong>Gesamt</strong></td>
                    <td class="text-h6"><strong>{{ totalWeaponsCount }}</strong></td>
                    <td>-</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-divider class="my-6"></v-divider>

      <!-- 10-Jahres-Regel Banner -->
      <v-alert
        v-if="meta.ten_years_reached"
        color="success"
        dark
        border="left"
        elevation="2"
        class="mb-6"
      >
        <div class="d-flex align-center">
          <v-icon large class="mr-4">mdi-shield-check</v-icon>
          <div>
            <div class="text-h6">Bedürfnisprüfung vereinfacht</div>
            <div>
              Die 10-Jahres-Regel gemäß § 14 Abs. 4 WaffG wurde erreicht.
            </div>
          </div>
        </div>
      </v-alert>

      <!-- Ersterwerb (§ 14 Abs. 3) -->
      <v-card outlined class="mb-6 elevation-1">
        <v-card-title class="pb-2">
          Ersterwerb (§ 14 Abs. 3)
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" small class="ml-2"
                >mdi-information-outline
              </v-icon>
            </template>
            <span
              >Voraussetzung für den ersten Waffenerwerb: 12 Monate
              Mitgliedschaft und 18 Termine (oder 12x monatlich).</span
            >
          </v-tooltip>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-1 d-flex justify-space-between">
                <span>Schießtermine (letzte 12 Monate)</span>
                <span class="font-weight-bold"
                  >{{ comp_WaffGCompliance.initial_check.total_sessions }} / 18</span
                >
              </div>
              <v-progress-linear
                :value="
                  Math.min(
                    (comp_WaffGCompliance.initial_check.total_sessions / 18) * 100,
                    100
                  )
                "
                :color="
                  getStatusColor(
                    comp_WaffGCompliance.initial_check.total_sessions,
                    18
                  )
                "
                height="12"
                rounded
              ></v-progress-linear>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-1 d-flex justify-space-between">
                <span>Aktive Monate</span>
                <span class="font-weight-bold"
                  >{{ comp_WaffGCompliance.initial_check.months_active }} / 12</span
                >
              </div>
              <v-progress-linear
                :value="
                  Math.min(
                    (comp_WaffGCompliance.initial_check.months_active / 12) * 100,
                    100
                  )
                "
                :color="
                  getStatusColor(comp_WaffGCompliance.initial_check.months_active, 12)
                "
                height="12"
                rounded
              ></v-progress-linear>
            </v-col>
          </v-row>

          <div
            v-if="comp_WaffGCompliance.initial_check.remaining_for_18 > 0"
            class="mt-4 text-center"
          >
            <v-chip outlined color="orange">
              Noch
              <b class="mx-1">{{
                comp_WaffGCompliance.initial_check.remaining_for_18
              }}</b>
              Termine bis zum Erwerb
            </v-chip>
          </div>
          <div v-else class="mt-4 text-center">
            <v-chip color="success" dark
              >Voraussetzungen für Ersterwerb erfüllt
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Fortbestehen (§ 14 Abs. 4 & 5) -->
      <h3 class="text-h5 mb-4 px-2">Fortbestehen des Bedürfnisses</h3>

      <!-- NEU: Waffen-Liste: Bedürfnis-Filter anzeigen -->
      <v-card outlined class="mb-6 elevation-1">
        <v-card-title>
          Waffen-Übersicht & Compliance
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="comp_ComplianceWeapons"
          class="elevation-0"
          hide-default-footer
          dense
        >
          <!-- Bedürfnis-Spalte mit Filter-Hinweis -->
          <template v-slot:item.beduerfnis="{ item }">
            <v-chip
              x-small
              :color="item.beduerfnis === 'sport' ? 'success' : 'grey'"
              :outlined="item.beduerfnis !== 'sport'"
            >
              {{ getBeduerfnisLabel(item.beduerfnis) }}
            </v-chip>
          </template>

          <!-- Compliance-Status -->
          <template v-slot:item.compliance="{ item }">
            <div v-if="item.beduerfnis === 'sport'">
              <v-chip
                small
                :color="item.is_compliant ? 'success' : 'error'"
                text-color="white"
              >
                <v-icon left small>
                  {{ item.is_compliant ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
                {{ item.is_compliant ? 'Erfüllt' : 'Nicht erfüllt' }}
              </v-chip>
            </div>

            <!-- Hinweis bei Nicht-Sport-Waffen -->
            <div v-else class="text-caption grey--text">
              Nicht prüfpflichtig ({{ getBeduerfnisLabel(item.beduerfnis) }})
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-row>
        <!-- Kurzwaffe (KW) Section -->
        <v-col cols="12" md="6" v-if="comp_WaffGData.meta.has_kw">
          <v-card outlined height="100%">
            <v-toolbar flat dense color="grey lighten-4">
              <v-icon left>mdi-pistol</v-icon>
              <v-toolbar-title>Kurzwaffen (KW)</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip
                v-if="
                  comp_WaffGData.recurrent_check.kw.recurrent_basic.compliant
                "
                color="success"
                x-small
                dark
              >
                Erfüllt
              </v-chip>
            </v-toolbar>

            <v-card-text>
              <div class="mb-4">
                <div class="text-caption grey--text text--darken-1 mb-1">
                  Grundbedürfnis (Termine pro Jahr)
                </div>
                <div class="d-flex align-center mb-2">
                  <div class="flex-grow-1 mr-4">
                    <div class="d-flex justify-space-between text-caption">
                      <span>Jahr 1 (akt.)</span>
                      <span
                        >{{
                          comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_1_count
                        }}
                        / 6</span
                      >
                    </div>
                    <v-progress-linear
                      :value="
                        Math.min(
                          (comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_1_count /
                            6) *
                            100,
                          100
                        )
                      "
                      :color="
                        getStatusColor(
                          comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_1_count,
                          6
                        )
                      "
                      height="8"
                      rounded
                    ></v-progress-linear>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-space-between text-caption">
                      <span>Jahr 2</span>
                      <span
                        >{{
                          comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_2_count
                        }}
                        / 6</span
                      >
                    </div>
                    <v-progress-linear
                      :value="
                        Math.min(
                          (comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_2_count /
                            6) *
                            100,
                          100
                        )
                      "
                      :color="
                        getStatusColor(
                          comp_WaffGData.recurrent_check.kw.recurrent_basic
                            .year_2_count,
                          6
                        )
                      "
                      height="8"
                      rounded
                    ></v-progress-linear>
                  </div>
                </div>
              </div>

              <!-- Überkontingent KW -->
              <div
                v-if="
                  comp_WaffGData.recurrent_check.kw.over_contingent_check.active
                "
                class="pa-3 rounded bg-blue-50 border-blue-200 border"
              >
                <div class="d-flex align-center mb-2">
                  <v-icon color="amber darken-2" class="mr-2"
                    >mdi-trophy</v-icon
                  >
                  <span class="font-weight-bold"
                    >Wettbewerbe (Überkontingent)</span
                  >
                </div>
                <div class="text-caption mb-2">
                  Nachweis von Wettbewerben erforderlich.
                </div>
                <div class="d-flex justify-space-around text-center">
                  <div>
                    <div class="text-h6">
                      {{
                        comp_WaffGData.recurrent_check.kw.over_contingent_check
                          .year_1_competitions
                      }}
                    </div>
                    <div class="text-caption">Jahr 1</div>
                  </div>
                  <div>
                    <div class="text-h6">
                      {{
                        comp_WaffGData.recurrent_check.kw.over_contingent_check
                          .year_2_competitions
                      }}
                    </div>
                    <div class="text-caption">Jahr 2</div>
                  </div>
                </div>
              </div>
              <!-- Bonus Anzeige wenn nicht aktiv -->
              <div v-else class="text-caption grey--text">
                Wettbewerbe gesamt:
                {{
                  comp_WaffGData.recurrent_check.kw.over_contingent_check
                    .year_1_competitions +
                    comp_WaffGData.recurrent_check.kw.over_contingent_check
                      .year_2_competitions
                }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Langwaffe (LW) Section -->
        <v-col cols="12" md="6" v-if="comp_WaffGData.meta.has_lw">
          <v-card outlined height="100%">
            <v-toolbar flat dense color="grey lighten-4">
              <v-icon left>mdi-rifle</v-icon>
              <v-toolbar-title>Langwaffen (LW)</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip
                v-if="
                  comp_WaffGData.recurrent_check.lw.recurrent_basic.compliant
                "
                color="success"
                x-small
                dark
              >
                Erfüllt
              </v-chip>
            </v-toolbar>

            <v-card-text>
              <div class="mb-4">
                <div class="text-caption grey--text text--darken-1 mb-1">
                  Grundbedürfnis (Termine pro Jahr)
                </div>
                <div class="d-flex align-center mb-2">
                  <div class="flex-grow-1 mr-4">
                    <div class="d-flex justify-space-between text-caption">
                      <span>Jahr 1 (akt.)</span>
                      <span
                        >{{
                          comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_1_count
                        }}
                        / 6</span
                      >
                    </div>
                    <v-progress-linear
                      :value="
                        Math.min(
                          (comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_1_count /
                            6) *
                            100,
                          100
                        )
                      "
                      :color="
                        getStatusColor(
                          comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_1_count,
                          6
                        )
                      "
                      height="8"
                      rounded
                    ></v-progress-linear>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex justify-space-between text-caption">
                      <span>Jahr 2</span>
                      <span
                        >{{
                          comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_2_count
                        }}
                        / 6</span
                      >
                    </div>
                    <v-progress-linear
                      :value="
                        Math.min(
                          (comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_2_count /
                            6) *
                            100,
                          100
                        )
                      "
                      :color="
                        getStatusColor(
                          comp_WaffGData.recurrent_check.lw.recurrent_basic
                            .year_2_count,
                          6
                        )
                      "
                      height="8"
                      rounded
                    ></v-progress-linear>
                  </div>
                </div>
              </div>

              <!-- Überkontingent LW -->
              <div
                v-if="
                  comp_WaffGData.recurrent_check.lw.over_contingent_check.active
                "
                class="pa-3 rounded bg-blue-50 border-blue-200 border"
              >
                <div class="d-flex align-center mb-2">
                  <v-icon color="amber darken-2" class="mr-2">mdi-medal</v-icon>
                  <span class="font-weight-bold"
                    >Wettbewerbe (Überkontingent)</span
                  >
                </div>
                <div class="text-caption mb-2">
                  Nachweis von Wettbewerben erforderlich.
                </div>
                <div class="d-flex justify-space-around text-center">
                  <div>
                    <div class="text-h6">
                      {{
                        comp_WaffGData.recurrent_check.lw.over_contingent_check
                          .year_1_competitions
                      }}
                    </div>
                    <div class="text-caption">Jahr 1</div>
                  </div>
                  <div>
                    <div class="text-h6">
                      {{
                        comp_WaffGData.recurrent_check.lw.over_contingent_check
                          .year_2_competitions
                      }}
                    </div>
                    <div class="text-caption">Jahr 2</div>
                  </div>
                </div>
              </div>
              <!-- Bonus Anzeige wenn nicht aktiv -->
              <div v-else class="text-caption grey--text">
                Wettbewerbe gesamt:
                {{
                  comp_WaffGData.recurrent_check.lw.over_contingent_check
                    .year_1_competitions +
                    comp_WaffGData.recurrent_check.lw.over_contingent_check
                      .year_2_competitions
                }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Rechtliche Hinweise Footer -->
      <v-expansion-panels class="mt-8" flat>
        <v-expansion-panel class="transparent">
          <v-expansion-panel-header class="grey--text text--darken-1">
            <v-icon left small>mdi-help-circle</v-icon>
            Welche Waffen werden geprüft?
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Bedürfnis</th>
                    <th>Gesetzesgrundlage</th>
                    <th>Prüfpflichtig?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <v-chip x-small color="success">Sportschütze</v-chip>
                    </td>
                    <td>§14 WaffG</td>
                    <td>
                      <v-icon color="success">mdi-check-circle</v-icon>
                      <strong>Ja</strong> - Nachweispflicht!
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip x-small outlined>Jäger</v-chip>
                    </td>
                    <td>§13 WaffG</td>
                    <td>
                      <v-icon color="grey">mdi-close-circle</v-icon>
                      Nein - Andere Regelung
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip x-small outlined>Sammler</v-chip>
                    </td>
                    <td>§17 WaffG</td>
                    <td>
                      <v-icon color="grey">mdi-close-circle</v-icon>
                      Nein - Sammlungswaffen
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip x-small outlined>Hersteller/Händler</v-chip>
                    </td>
                    <td>§21 WaffG</td>
                    <td>
                      <v-icon color="grey">mdi-close-circle</v-icon>
                      Nein - Gewerblich
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip x-small outlined>Gefährdete Person</v-chip>
                    </td>
                    <td>§19 WaffG</td>
                    <td>
                      <v-icon color="grey">mdi-close-circle</v-icon>
                      Nein - Eigenschutz
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert type="info" dense outlined class="mt-4">
              <strong>Wichtig:</strong> Vereinswaffen und Pool-Waffen werden
              für die Nachweispflicht berücksichtigt (§14 Abs. 3 WaffG).
            </v-alert>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="transparent">
          <v-expansion-panel-header class="grey--text text--darken-1">
            <span
              ><v-icon small class="mr-1">mdi-gavel</v-icon> Rechtliche Hinweise
              zu § 14 WaffG</span
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content
            class="text-caption grey--text text--darken-2"
          >
            <p>
              <b>§ 14 Abs. 3 (Erwerb):</b> Für den ersten Erwerb einer Waffe ist
              eine 12-monatige regelmäßige Vereinszugehörigkeit mit mindestens
              18 Terminen im Jahr oder einmal pro Monat nachzuweisen.
            </p>
            <p>
              <b>§ 14 Abs. 4 (Regelprüfung):</b> Die Behörde prüft alle 5 Jahre
              das Fortbestehen des Bedürfnisses. Nach 10 Jahren reicht für das
              Grundbedürfnis eine Bestätigung der Vereinsmitgliedschaft aus.
            </p>
            <p>
              <b>§ 14 Abs. 5 (Überkontingent):</b> Wer mehr als das
              Regelbedürfnis (3 halbautomatische Langwaffen / 2 mehrschüssige
              Kurzwaffen für Patronenmunition) besitzt, muss die regelmäßige
              Teilnahme an Schießsportwettbewerben nachweisen.
            </p>

            <v-divider class="my-3"></v-divider>

            <div
              class="d-flex align-start red--text text--darken-2 pa-2 rounded-lg grey lighten-4"
            >
              <v-icon small color="red darken-2" class="mr-2 mt-1"
                >mdi-alert-octagon</v-icon
              >
              <div>
                <b class="text-uppercase">Wichtiger Hinweis (Disclaimer):</b
                ><br />
                Diese Auswertung dient ausschließlich als unverbindliche
                Orientierungshilfe auf Basis der im digitalen Schießbuch
                erfassten Daten. Sie stellt keine Rechtsberatung dar und ersetzt
                nicht die Prüfung durch die zuständige Waffenbehörde oder Ihren
                Verband. Für die Richtigkeit der Angaben sowie die Einhaltung
                aller gesetzlichen Fristen und Anforderungen gemäß WaffG ist
                jeder Schütze eigenverantwortlich. Der Betreiber übernimmt keine
                Haftung für aus dieser Anzeige resultierende rechtliche
                Konsequenzen.
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Fallback -->
    <v-card v-else outlined class="pa-10 text-center grey--text">
      <v-icon x-large class="mb-4">mdi-database-off-outline</v-icon>
      <div>Keine Compliance-Daten verfügbar.</div>
      <v-btn text color="primary" class="mt-4" @click="get_ComplianceData">
        Erneut versuchen
      </v-btn>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "WaffGComplianceDashboard",
  data() {
    return {
      headers: [
        { text: 'Waffe', value: 'name', align: 'start' },
        { text: 'Typ', value: 'gattung' },
        { text: 'Kaliber', value: 'kaliber' },
        { text: 'Bedürfnis', value: 'beduerfnis' },
        { text: 'Status', value: 'compliance', sortable: false }
      ]
    };
  },
  computed: {
    comp_WaffGCompliance() {
      return this.$store.getters['UserApp/has_WaffGCompliance'] || {};
    },

    // NEU: Meta-Daten direkt aus Response
    meta() {
      return this.comp_WaffGCompliance.meta || {};
    },

    counts() {
      return this.meta.counts || { sport_weapons: 0, other_weapons: 0, total_weapons: 0 };
    },

    // Direkte Zugriffe
    sportWeaponsCount() {
      return this.counts.sport_weapons;
    },

    otherWeaponsCount() {
      return this.counts.other_weapons;
    },

    totalWeaponsCount() {
      return this.counts.total_weapons;
    },

    infoText() {
      return this.meta.info || '';
    },

    comp_WaffGData() {
      return this.comp_WaffGCompliance;
    },
    comp_loading() {
      return this.$store.state.UserApp.shootingbook_loader;
    },
    comp_ComplianceWeapons() {
      return (this.comp_WaffGCompliance && this.comp_WaffGCompliance.weapons) || [];
    },
    comp_BeduerfnisOptions() {
      return this.$store.getters['UserApp/beduerfnisOptions'];
    }
  },
  mounted() {
    this.get_ComplianceData();
  },
  methods: {
    getBeduerfnisLabel(value) {
      if (!this.comp_BeduerfnisOptions) return value;
      const option = this.comp_BeduerfnisOptions.find(o => o.value === value);
      return option ? option.label : value;
    },
    getStatusColor(current, target) {
      const ratio = current / target;
      if (ratio >= 1) return "success";
      if (ratio >= 0.5) return "warning";
      return "error";
    },
    get_ComplianceData() {
      this.$store.dispatch("UserApp/get_WaffGCompliance");
    }
  }
};
</script>

<style scoped>
.bg-blue-50 {
  background-color: #f0f7ff;
}

.border-blue-200 {
  border-color: #bee3f8;
}

.p-4 {
  padding: 1rem;
}

.max-w-4xl {
  max-width: 56rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>